/**
 * <s-recently-viewed> — remembers the cakes a shopper opened (in this browser only, localStorage)
 * and shows up to four of them, excluding the current product. Nothing renders until there is history.
 * Attributes (written by Liquid): data-handle (current product), data-products-url (routes.root_url + products),
 * data-currency, data-from-label. Product data comes from the storefront's product .js endpoint.
 */
const STORAGE_KEY = 'lc-recently-viewed';
const MAX_STORED = 12;
const MAX_SHOWN = 4;

class SRecentlyViewed extends HTMLElement {
  #ac = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const current = this.dataset.handle;
    let history = [];
    try {
      history = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(history)) history = [];
    } catch (e) {
      history = [];
    }
    const others = history.filter((h) => typeof h === 'string' && h !== current).slice(0, MAX_SHOWN);
    if (current) {
      const next = [current, ...history.filter((h) => h !== current)].slice(0, MAX_STORED);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (e) { /* storage unavailable */ }
    }
    if (others.length) this.#render(others);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #render(handles) {
    const base = (this.dataset.productsUrl || '').replace(/\/$/, '');
    const list = this.querySelector('[data-list]');
    if (!list || !base) return;
    const results = await Promise.all(handles.map(async (handle) => {
      try {
        const res = await fetch(`${base}/${encodeURIComponent(handle)}.js`, { signal: this.#ac.signal });
        if (!res.ok) return null;
        return await res.json();
      } catch (e) {
        return null;
      }
    }));
    const products = results.filter((p) => p && p.available !== undefined);
    if (!products.length) return;
    const money = new Intl.NumberFormat('en-IN', { style: 'currency', currency: this.dataset.currency || 'INR', maximumFractionDigits: 0 });
    const fromLabel = this.dataset.fromLabel || '';
    list.replaceChildren(...products.map((p) => {
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = p.url;
      a.className = 'group/rv flex h-full flex-col gap-3 no-underline text-on-surface focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent rounded-card';
      const media = document.createElement('span');
      media.className = 'block aspect-[4/5] overflow-hidden rounded-card bg-surface-secondary';
      if (p.featured_image) {
        const img = document.createElement('img');
        const src = p.featured_image.startsWith('//') ? `https:${p.featured_image}` : p.featured_image;
        img.src = `${src}${src.includes('?') ? '&' : '?'}width=480`;
        img.alt = p.title;
        img.loading = 'lazy';
        img.width = 480;
        img.height = 600;
        img.className = 'size-full object-cover motion-safe:transition-transform motion-safe:duration-500 group-hover/rv:scale-105';
        media.append(img);
      }
      const title = document.createElement('span');
      title.className = 'font-heading text-base leading-tight line-clamp-2';
      title.textContent = p.title;
      const price = document.createElement('span');
      price.className = 'text-sm font-medium';
      const amount = money.format((p.price_min ?? p.price) / 100);
      price.textContent = p.price_varies && fromLabel ? `${fromLabel} ${amount}` : amount;
      a.append(media, title, price);
      li.append(a);
      return li;
    }));
    this.hidden = false;
  }
}

if (!customElements.get('s-recently-viewed')) customElements.define('s-recently-viewed', SRecentlyViewed);
